import React, { useCallback, useState } from 'react';
import { Modal } from '../_custom';
import Tooltip from '../_custom/Tooltip/Tooltip';
import styles from './ComposeEmailModal.module.scss';
import { truncateByCharacters } from '../../utils/utils';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import { PlateEditor } from '../PlateEditor';
import { plateToHtml } from '../PlateEditor';
import type { MentionUser } from '../PlateEditor';
import { COMPOSE_PITCH_CHIP } from '../PlateEditor/ai/aiMenuConstants';
import type { Value } from 'platejs';
import { useComposeEmailLogic } from './useComposeEmailLogic';
import { isComposeBodyEmpty } from './composeEmailUtils';
import { buildMagicSignPreviewHtml, openMagicSignPreviewTab } from './composeEmailPreview';
import { COMPOSE_EMAIL_BODY_PLACEHOLDER, COMPOSE_EMAIL_UPLOAD_SOURCE, COMPOSE_EMAIL_DRAWER_SOURCE } from './constants';
import { ATTACHMENT_OVER_LIMIT_MESSAGE, HEADER_NAME_SEPARATOR } from './ComposeEmailModal.constants';
import type { ComposeEmailModalProps, NonEditableLink } from './types';
import UserPillsDisplay from './recipients/UserPillsDisplay';
import AttachmentDropdown from './attachments/AttachmentDropdown';
import SignatureDropdown from './signature/SignatureDropdown';
import AttachmentPills from './attachments/AttachmentPills';
import { useAppSelector } from '../../app/hooks';
import EngagementLetter from '../EngagementLetter/EngagementLetter';
import BureauKycConsent from '../BureauKycConsent/BureauKycConsent';
import { useFeatureVisibility } from '../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../utils/constants/visibilityKeys';
import { COMPOSE_TOOLBAR_HOST_ID } from './constants';
import { dataAlertIcon } from '../../assets/hostedassets';

// Displays the organization name + "Email" label in the modal header.
// Extended for Magic Sign: headerTitle replaces "Email", headerSubTitle replaces orgName.
// borrowerName (Deals page only) renders as "Company | Lender".
const ModalHeader: React.FC<{ orgName: string; borrowerName?: string; headerTitle?: string; headerSubTitle?: string }> = ({
    orgName,
    borrowerName,
    headerTitle,
    headerSubTitle,
}) => {
    const segments = headerSubTitle ? [headerSubTitle] : ([borrowerName, orgName].filter(Boolean) as string[]);
    const tooltipContent = segments.join(HEADER_NAME_SEPARATOR);
    const displayText = segments.map((segment) => truncateByCharacters(segment, 30)).join(HEADER_NAME_SEPARATOR);

    return (
        <div className={styles.ComposeEmailModalHeader}>
            <div className={styles.EmailContainer}>
                <span className={styles.Email}>{headerTitle ?? 'Email'}</span>
                <Tooltip content={tooltipContent} direction="right" fitContent={true}>
                    <span className={styles.CompanyName}>{displayText}</span>
                </Tooltip>
            </div>
        </div>
    );
};

// Displays the users in the modal header with pill-based UI
const UsersSection: React.FC<{
    selectedUsers: any[];
    selectableUsers: any[];
    onAddUser: (email: string, isExternal?: boolean) => Promise<void> | void;
    onRemoveUser: (email: string) => void;
    inputValue: string;
    showDropdown: boolean;
    filteredUsers: string[];
    selectedPills: Set<string>;
    isValidatingEmail: boolean;
    inputRef: React.RefObject<HTMLInputElement>;
    isSelectingFromDropdown: React.MutableRefObject<boolean>;
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    handleDropdownItemClick: (email: string) => void;
    handleInputBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    handleInputFocus: () => void;
    handlePillClick: (email: string, e: React.MouseEvent) => void;
    handleCloseDropdown: () => void;
    setSelectedPills: React.Dispatch<React.SetStateAction<Set<string>>>;
}> = ({
    selectedUsers,
    selectableUsers,
    onAddUser,
    onRemoveUser,
    inputValue,
    showDropdown,
    filteredUsers,
    selectedPills,
    isValidatingEmail,
    inputRef,
    isSelectingFromDropdown,
    handleInputChange,
    handleKeyPress,
    handleDropdownItemClick,
    handleInputBlur,
    handleInputFocus,
    handlePillClick,
    handleCloseDropdown,
    setSelectedPills,
}) => {
    return (
        <div className={styles.UsersInfo}>
            <UserPillsDisplay
                selectedUsers={selectedUsers}
                selectableUsers={selectableUsers}
                onAddUser={onAddUser}
                onRemoveUser={onRemoveUser}
                inputValue={inputValue}
                showDropdown={showDropdown}
                filteredUsers={filteredUsers}
                selectedPills={selectedPills}
                isValidatingEmail={isValidatingEmail}
                inputRef={inputRef}
                isSelectingFromDropdown={isSelectingFromDropdown}
                handleInputChange={handleInputChange}
                handleKeyPress={handleKeyPress}
                handleDropdownItemClick={handleDropdownItemClick}
                handleInputBlur={handleInputBlur}
                handleInputFocus={handleInputFocus}
                handlePillClick={handlePillClick}
                handleCloseDropdown={handleCloseDropdown}
                setSelectedPills={setSelectedPills}
            />
        </div>
    );
};

/**
 * Non-editable per-recipient signing links section.
 * Shown in the Magic Sign "Send for Signing" flow below the PlateEditor.
 * Mirrors the DataPendingSection container style — same .SendNeedInfo wrapper.
 */
const NonEditableLinksSection: React.FC<{ links: NonEditableLink[] }> = ({ links }) => {
    if (!links.length) return null;
    return (
        <div className={styles.SendNeedInfo}>
            <div className={styles.Consent}>
                <div className={styles.Text}>
                    <img src={dataAlertIcon} alt="signing-links" />
                    <span className={styles.ConsentText}>Per-signatory signing links (auto-included, non-editable)</span>
                </div>
            </div>
            <ul className={styles.NonEditableLinksList}>
                {links.map((sl, idx) => (
                    <li key={sl.link + idx} className={styles.NonEditableLinkItem}>
                        <span className={styles.NonEditableLinkName}>{sl.name}</span>
                        {sl.orgName && <span className={styles.NonEditableLinkOrg}>&nbsp;&mdash;&nbsp;{sl.orgName}</span>}
                        <span className={styles.NonEditableLinkUrl}>{sl.link}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

// Action buttons to preview and send the email
const ActionButtons: React.FC<{
    onPreview: () => void;
    onSend: () => void;
    isPreviewLoading?: boolean;
    isSendLoading?: boolean;
    isUploadingFiles?: boolean;
    userInputText: Value;
    sendNeedInfo: boolean;
    selectedUsers: any[];
    isOverAttachmentLimit?: boolean;
}> = ({
    onPreview,
    onSend,
    isPreviewLoading = false,
    isSendLoading = false,
    isUploadingFiles = false,
    userInputText,
    sendNeedInfo,
    selectedUsers,
    isOverAttachmentLimit = false,
}) => {
    const isEmptyContent = isComposeBodyEmpty(userInputText);
    const noUsersMessage = 'No recipients selected. Please select at least one recipient before composing email.';

    return (
        <div className={styles.SendPreviewButtons}>
            <div className={styles.ButtonContainer}>
                <Tooltip content={!selectedUsers.length ? noUsersMessage : ''} direction="bottom">
                    <button
                        className={styles.Preview}
                        onClick={onPreview}
                        disabled={
                            isPreviewLoading ||
                            isSendLoading ||
                            isUploadingFiles ||
                            (isEmptyContent && !sendNeedInfo) ||
                            selectedUsers.length === 0
                        }
                    >
                        {isPreviewLoading ? <LoadingSpinner height="12px" color="#6021B3" /> : 'Preview Email'}
                    </button>
                </Tooltip>
                <Tooltip
                    content={!selectedUsers.length ? noUsersMessage : isOverAttachmentLimit ? ATTACHMENT_OVER_LIMIT_MESSAGE : ''}
                    direction="right"
                    fitContent
                >
                    <button
                        className={styles.Send}
                        onClick={onSend}
                        disabled={
                            isPreviewLoading ||
                            isSendLoading ||
                            isUploadingFiles ||
                            (isEmptyContent && !sendNeedInfo) ||
                            selectedUsers.length === 0 ||
                            isOverAttachmentLimit
                        }
                    >
                        {isSendLoading ? <LoadingSpinner height="12px" color="#FFFFFF" /> : 'Send'}
                    </button>
                </Tooltip>
            </div>
        </div>
    );
};

const ComposeEmailModal: React.FC<ComposeEmailModalProps> = ({
    isOpen,
    initialRecipients,
    nonEditableLinks,
    onSendOverride,
    onCloseOverride,
    headerTitle,
    headerSubTitle,
    initialNote,
}) => {
    const { aiEnabled } = useFeatureVisibility({ aiEnabled: VISIBILITY_KEYS.AI.ENABLED });
    // PNO-branded logo shown in the email preview header (matches the standard email's {{logoUrl}}).
    const pnoLogoUrl = useAppSelector((state) => state.user.domainMetadata?.logoUrl);
    const {
        isModalLoading,
        isPreviewLoading,
        isSendLoading,
        isUploadingFiles,
        isOverAttachmentLimit,
        handlePreview: handlePreviewDefault,
        handleSend: handleSendDefault,
        handleFileUpload,
        handleToggleSystemAttachment,
        handleToggleAttachSignature,
        handleRemoveManualFile,
        handleUploadStatusChange,
        handleAddUser,
        handleRemoveUser,
        selectedUsers,
        selectableUsers,
        ownerOptions,
        isLenderQuery,
        sendNeedInfo,
        visibleSystemAttachmentKeys,
        systemToggles,
        attachmentPills,
        handleAttachmentPillClick,
        attachSignature,
        userInputText,
        userInputTextRef,
        categoryId,
        flowId,
        closeComposeModal,
        composeEmailData,
        setUserInputText,
        handleEditorMount,
        orgId,
        getSignatureData,
        // User Email Input related
        inputValue,
        showDropdown,
        filteredUsers,
        selectedPills,
        isValidatingEmail,
        inputRef,
        isSelectingFromDropdown,
        handleInputChange,
        handleKeyPress,
        handleDropdownItemClick,
        handleInputBlur,
        handleInputFocus,
        handlePillClick,
        handleCloseDropdown,
        setSelectedPills,
    } = useComposeEmailLogic({ isOpen, initialRecipients, initialNote });

    // Local in-flight flag for the Magic Sign send path. Prevents duplicate signing
    // emails if the user double-clicks Send while plateToHtml + onSendOverride are
    // awaited. The hook's isSendLoading covers the standard Redux path; this covers
    // the override branch which the hook never touches.
    const [isMagicSignSending, setIsMagicSignSending] = useState(false);

    // When onSendOverride is provided (Magic Sign path), bypass the Redux email flow.
    // Convert the Plate Value to HTML via plateToHtml (async) before calling the override.
    const handleSend = useCallback(async () => {
        if (!onSendOverride) {
            handleSendDefault();
            return;
        }
        if (isMagicSignSending) return;
        setIsMagicSignSending(true);
        try {
            // Read the ref (not the debounced userInputText state) so a Send click
            // right after typing always serialises the latest editor content.
            const noteHtml = await plateToHtml(userInputTextRef.current || []);
            const safeNote = noteHtml === '<div></div>' ? '' : noteHtml;
            await onSendOverride(
                selectedUsers.map((u) => ({ emailId: u.emailId, userId: u.userId })),
                safeNote,
            );
        } finally {
            setIsMagicSignSending(false);
        }
    }, [onSendOverride, handleSendDefault, userInputTextRef, selectedUsers, isMagicSignSending]);

    // For Magic Sign path (onSendOverride + nonEditableLinks): open the exact email in a new
    // tab using buildMagicSignPreviewHtml, matching the general Data Pending preview behaviour.
    // For the standard path: delegate to handlePreviewDefault.
    const handlePreview = useCallback(async () => {
        if (onSendOverride) {
            // Read the ref (not the debounced userInputText state) so a Preview click
            // right after typing always renders the latest editor content.
            const noteHtml = await plateToHtml(userInputTextRef.current || []);
            const html = buildMagicSignPreviewHtml({
                recipients: selectedUsers.map((u) => ({ emailId: u.emailId, name: u.name })),
                subject: `Signing request: ${headerSubTitle ?? ''}`,
                noteHtml,
                links: nonEditableLinks ?? [],
                logoUrl: pnoLogoUrl ?? undefined,
            });
            openMagicSignPreviewTab(html);
            return;
        }
        handlePreviewDefault();
    }, [onSendOverride, nonEditableLinks, handlePreviewDefault, selectedUsers, headerSubTitle, userInputTextRef, pnoLogoUrl]);

    const hasNonEditableLinks = !!nonEditableLinks?.length;
    const effectiveOnClose = onCloseOverride ?? closeComposeModal;

    // System-attachment editor drawers reused from their existing surfaces. They
    // portal above this modal (z-index 10100 > 10000) on the COMPOSE_EMAIL source
    // and run the view-to-attach capture on Save & Close.
    const engagementLetterData = useAppSelector((state) => state.investor.engagementLetter);
    const bureauKycData = useAppSelector((state) => state.investor.bureauAndKYCConsentLetter);

    return (
        <>
            <Modal open={isOpen} onClose={effectiveOnClose} size="flexible" persistent>
                <div className={styles.ComposeEmailModalContainer}>
                    {isModalLoading ? (
                        <div className={styles.LoadingContainer}>
                            <LoadingSpinner height="24px" color="#6021B3" />
                        </div>
                    ) : (
                        <>
                            <ModalHeader
                                orgName={composeEmailData.orgName}
                                borrowerName={composeEmailData.borrowerName}
                                headerTitle={headerTitle}
                                headerSubTitle={headerSubTitle}
                            />
                            <UsersSection
                                selectedUsers={selectedUsers}
                                selectableUsers={selectableUsers}
                                onAddUser={handleAddUser}
                                onRemoveUser={handleRemoveUser}
                                inputValue={inputValue}
                                showDropdown={showDropdown}
                                filteredUsers={filteredUsers}
                                selectedPills={selectedPills}
                                isValidatingEmail={isValidatingEmail}
                                inputRef={inputRef}
                                isSelectingFromDropdown={isSelectingFromDropdown}
                                handleInputChange={handleInputChange}
                                handleKeyPress={handleKeyPress}
                                handleDropdownItemClick={handleDropdownItemClick}
                                handleInputBlur={handleInputBlur}
                                handleInputFocus={handleInputFocus}
                                handlePillClick={handlePillClick}
                                handleCloseDropdown={handleCloseDropdown}
                                setSelectedPills={setSelectedPills}
                            />
                            <div className={styles.ScrollableContent}>
                                <div className={styles.ToolbarHost} id={COMPOSE_TOOLBAR_HOST_ID} />
                                <div className={styles.UserInputText}>
                                    <PlateEditor
                                        compact
                                        toolbarVariant="slim"
                                        toolbarHostId={COMPOSE_TOOLBAR_HOST_ID}
                                        hiddenMarks={['code', 'subscript', 'superscript']}
                                        className={styles.BodyEditor}
                                        placeholder={COMPOSE_EMAIL_BODY_PLACEHOLDER}
                                        // Seed the editor with the pre-filled body (Magic Sign's greeting +
                                        // per-signatory signing links, via buildMagicSignNoteWithLinks) or a
                                        // plain-string note. userInputText is the hook's useState value, lazily
                                        // initialised from initialNote — at first render (before any onChange
                                        // fires) it IS the seeded value, and usePlateEditor only reads this prop
                                        // once at editor creation, so passing the state var here is safe. Callers
                                        // with no initialNote seed to EMPTY_EDITOR_VALUE, matching today's
                                        // no-prop (undefined → EMPTY_EDITOR_VALUE) behaviour exactly.
                                        initialValue={userInputText}
                                        uploadSource={
                                            isLenderQuery ? COMPOSE_EMAIL_UPLOAD_SOURCE.LENDER : COMPOSE_EMAIL_UPLOAD_SOURCE.COMPANY
                                        }
                                        onChange={(value: Value) => setUserInputText(value)}
                                        onEditorMount={handleEditorMount}
                                        mentionableUsers={ownerOptions as MentionUser[]}
                                        enableAI={Boolean(aiEnabled.write)}
                                        aiOrgId={orgId}
                                        aiExtraCompanyChips={[COMPOSE_PITCH_CHIP]}
                                    />
                                </div>
                                {hasNonEditableLinks && <NonEditableLinksSection links={nonEditableLinks!} />}
                            </div>
                            {attachmentPills.length > 0 && (
                                <div className={styles.AttachmentPillsBar}>
                                    <AttachmentPills
                                        pills={attachmentPills}
                                        onPillClick={handleAttachmentPillClick}
                                        onRemoveManual={handleRemoveManualFile}
                                        onRemoveSystem={(key) => handleToggleSystemAttachment(key, false)}
                                    />
                                </div>
                            )}
                            <div className={styles.Footer}>
                                <div className={styles.AttachmentBar}>
                                    <AttachmentDropdown
                                        visibleKeys={visibleSystemAttachmentKeys}
                                        toggles={systemToggles}
                                        onToggle={handleToggleSystemAttachment}
                                        orgId={orgId}
                                        categoryId={categoryId}
                                        flowId={flowId}
                                        onFileUpload={handleFileUpload}
                                        onUploadStatusChange={handleUploadStatusChange}
                                    />
                                    <SignatureDropdown
                                        attachSignature={attachSignature}
                                        onToggle={handleToggleAttachSignature}
                                        signatureData={getSignatureData()}
                                    />
                                </div>
                                <ActionButtons
                                    onPreview={handlePreview}
                                    onSend={handleSend}
                                    isPreviewLoading={isPreviewLoading}
                                    isSendLoading={isSendLoading || isMagicSignSending}
                                    isUploadingFiles={isUploadingFiles}
                                    userInputText={userInputText}
                                    sendNeedInfo={hasNonEditableLinks ? true : sendNeedInfo}
                                    selectedUsers={selectedUsers}
                                    isOverAttachmentLimit={isOverAttachmentLimit}
                                />
                            </div>
                        </>
                    )}
                </div>
            </Modal>
            {engagementLetterData?.open && engagementLetterData?.source === COMPOSE_EMAIL_DRAWER_SOURCE && <EngagementLetter />}
            {bureauKycData?.open && bureauKycData?.source === COMPOSE_EMAIL_DRAWER_SOURCE && <BureauKycConsent />}
        </>
    );
};

export default ComposeEmailModal;
